import {
  getAssetMetricsCmmsData,
  getAssetHierarchy,
} from '../api/asset-metrics-dashboardAPI';

let cachedResponse;
let cachedHierarchy;
// Derived relationship mappings from hierarchy data
let assetGroupToClassMap; // Map<assetGroupId, assetClassId>
let departmentToAreaMap; // Map<departmentId, areaId>

const loadData = async () => {
  if (!cachedResponse) {
    cachedResponse = await getAssetMetricsCmmsData();
  }
  return cachedResponse;
};

const loadHierarchy = async () => {
  if (!cachedHierarchy) {
    cachedHierarchy = await getAssetHierarchy();
    // Build relationship mappings from asset data
    buildRelationshipMaps(cachedHierarchy);
  }
  return cachedHierarchy;
};

/**
 * Build mappings for assetGroup -> assetClass and department -> area
 * These are derived from the assets array since each asset includes its full hierarchy
 */
const buildRelationshipMaps = (hierarchy) => {
  assetGroupToClassMap = new Map();
  departmentToAreaMap = new Map();

  const assets = hierarchy?.assets || [];
  assets.forEach((asset) => {
    // Map asset group to its asset class
    if (asset.assetGroup && asset.assetClass) {
      assetGroupToClassMap.set(asset.assetGroup, asset.assetClass);
    }
    // Map department to its area
    if (asset.department && asset.area) {
      departmentToAreaMap.set(asset.department, asset.area);
    }
  });
};

const deriveAssets = (data) => data?.assets || [];

const assetMetricsCmmsDataProvider = {
  async getAssetTypes() {
    const hierarchy = await loadHierarchy();
    return hierarchy?.assetTypes || [];
  },

  async getAssetClasses() {
    const hierarchy = await loadHierarchy();
    return hierarchy?.assetClasses || [];
  },

  async getAssetGroups(assetClassId) {
    const hierarchy = await loadHierarchy();
    const allGroups = hierarchy?.assetGroups || [];

    // If no assetClassId filter, return all groups
    if (!assetClassId) {
      return allGroups;
    }

    // Filter groups by assetClassId using the relationship mapping
    return allGroups.filter((group) => {
      const groupClass = assetGroupToClassMap.get(group.id);
      return groupClass === assetClassId;
    });
  },

  async getAreas() {
    const hierarchy = await loadHierarchy();
    return hierarchy?.areas || [];
  },

  async getDepartments(areaId) {
    const hierarchy = await loadHierarchy();
    const allDepartments = hierarchy?.departments || [];

    // If no areaId filter, return all departments
    if (!areaId) {
      return allDepartments;
    }

    // Filter departments by areaId using the relationship mapping
    return allDepartments.filter((dept) => {
      const deptArea = departmentToAreaMap.get(dept.id);
      return deptArea === areaId;
    });
  },

  async getAssets(filter) {
    const hierarchy = await loadHierarchy();
    const hierarchyAssets = hierarchy?.assets || [];

    // If no filter provided, return all assets
    if (!filter) {
      return hierarchyAssets.map((asset) => ({
        id: asset.id,
        assetName: asset.name,
        assetSerial: asset.serial,
        assetDescription: asset.assetDescription,
        assetImage: asset.assetImage,
      }));
    }

    const {
      assetTypeId,
      assetClassId,
      assetGroupId,
      departmentId,
      areaId,
    } = filter;

    // Filter assets based on the provided criteria
    const filteredAssets = hierarchyAssets.filter((asset) => {
      if (assetTypeId && asset.assetType !== assetTypeId) return false;
      if (assetClassId && asset.assetClass !== assetClassId) return false;
      if (assetGroupId && asset.assetGroup !== assetGroupId) return false;
      if (departmentId && asset.department !== departmentId) return false;
      if (areaId && asset.area !== areaId) return false;
      return true;
    });

    return filteredAssets.map((asset) => ({
      id: asset.id,
      assetName: asset.name,
      assetSerial: asset.serial,
      assetDescription: asset.assetDescription,
      assetImage: asset.assetImage,
    }));
  },

  async loadAssetsForFilters(filter) {
    const data = await loadData();
    const assets = deriveAssets(data);

    // If no filters are provided, return all assets
    if (!filter) {
      return assets;
    }

    const {
      assetTypeId,
      assetClassId,
      assetGroupId,
      departmentId,
      areaId,
      assetId,
      assetIds,
    } = filter;

    // Normalize assetId (singular) and assetIds (array) into a single array
    // The npm package passes assetId (singular), while some code may pass assetIds (array)
    let normalizedAssetIds = assetIds || [];
    if (assetId) {
      normalizedAssetIds = [assetId];
    }

    // Check if any hierarchy filters are specified
    const hasHierarchyFilters =
      assetTypeId !== undefined ||
      assetClassId !== undefined ||
      assetGroupId !== undefined ||
      departmentId !== undefined ||
      areaId !== undefined;

    // If no filters at all, return all assets
    if (!hasHierarchyFilters && !normalizedAssetIds?.length) {
      return assets;
    }

    // Build the set of allowed asset IDs based on filters
    let allowedAssetIds = null;

    // If hierarchy filters are present, use hierarchy data to find matching assets
    if (hasHierarchyFilters) {
      const hierarchy = await loadHierarchy();
      const hierarchyAssets = hierarchy?.assets || [];

      // Filter hierarchy assets based on the filter criteria
      const matchingHierarchyAssets = hierarchyAssets.filter((asset) => {
        if (assetTypeId !== undefined && asset.assetType !== assetTypeId) {
          return false;
        }
        if (assetClassId !== undefined && asset.assetClass !== assetClassId) {
          return false;
        }
        if (assetGroupId !== undefined && asset.assetGroup !== assetGroupId) {
          return false;
        }
        if (departmentId !== undefined && asset.department !== departmentId) {
          return false;
        }
        if (areaId !== undefined && asset.area !== areaId) {
          return false;
        }
        return true;
      });

      allowedAssetIds = new Set(matchingHierarchyAssets.map((a) => String(a.id)));
    }

    // If specific asset IDs are provided, intersect with hierarchy results
    if (normalizedAssetIds?.length) {
      const assetIdSet = new Set(normalizedAssetIds.map(String));
      if (allowedAssetIds) {
        // Intersect: only keep assets that are in both sets
        allowedAssetIds = new Set(
          [...allowedAssetIds].filter((id) => assetIdSet.has(id))
        );
      } else {
        allowedAssetIds = assetIdSet;
      }
    }

    // Filter the CMMS data assets by the allowed set
    if (allowedAssetIds) {
      return assets.filter((asset) => allowedAssetIds.has(asset.id));
    }

    return assets;
  },

  async getAvailableMeters(filter) {
    const assets = await this.loadAssetsForFilters(filter);

    //Modified to return unique meters only
//    return assets.flatMap((asset) => asset.meters?.map(({ id, unit }) => ({ id, unit })) || []);
    const allMeters = assets.flatMap((asset) => asset.meters?.map(({ id, unit }) => ({ id, unit })) || []);
    return [...new Map(allMeters.map((m) => [m.id, m])).values()];
},

  resolveAssetTypeName(assetTypeId) {
    if (!cachedHierarchy?.assetTypes) return undefined;
    const found = cachedHierarchy.assetTypes.find((t) => t.id === assetTypeId);
    return found?.name;
  },

  resolveAssetClassName(assetClassId) {
    if (!cachedHierarchy?.assetClasses) return undefined;
    const found = cachedHierarchy.assetClasses.find((c) => c.id === assetClassId);
    return found?.name;
  },

  resolveAssetGroupName(assetGroupId) {
    if (!cachedHierarchy?.assetGroups) return undefined;
    const found = cachedHierarchy.assetGroups.find((g) => g.id === assetGroupId);
    return found?.name;
  },

  resolveAreaName(areaId) {
    if (!cachedHierarchy?.areas) return undefined;
    const found = cachedHierarchy.areas.find((a) => a.id === areaId);
    return found?.name;
  },

  resolveDepartmentName(departmentId) {
    if (!cachedHierarchy?.departments) return undefined;
    const found = cachedHierarchy.departments.find((d) => d.id === departmentId);
    return found?.name;
  },

  resolveAssetName(_assetTypeId, _assetClassId, _assetGroupId, _areaId, _departmentId, assetId) {
    if (!cachedHierarchy?.assets) return assetId;
    const found = cachedHierarchy.assets.find((a) => a.id === assetId);
    if (!found) return assetId;
    return `${found.name} (${found.serial})`;
  },

  getAssetGroupingMetadata(assetId) {
    if (!cachedHierarchy?.assets || !assetId) return undefined;

    const found = cachedHierarchy.assets.find(
      (a) => String(a.id) === String(assetId)
    );
    if (!found) return undefined;

    // Return the grouping metadata matching the cmmsStub.ts interface
    return {
      id: found.id,
      assetName: found.name,
      assetSerial: found.serial,
      assetDescription: found.assetDescription,
      assetImage: found.assetImage,
      assetTypeId: found.assetType,
      assetGroupId: found.assetGroup,
      departmentId: found.department,
      // Denormalized display names for grouping
      assetGroup: found.assetGroup,
      assetClass: found.assetClass,
      department: found.department,
    };
  },
};

export const __resetAssetMetricsCache = () => {
  cachedResponse = undefined;
  cachedHierarchy = undefined;
  assetGroupToClassMap = undefined;
  departmentToAreaMap = undefined;
};

export default assetMetricsCmmsDataProvider;
